import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import Image from 'next/image';
import { clientFetch, clientPost, API_ENDPOINTS } from '@/lib/api';
import { toast } from './CustomToast';

/**
 * SectionContact Component
 * Contact form and information section
 */
const SectionContact: React.FC = () => {
  const logoUrl = "/image/logo-black.webp";
  const pattern = "/image/pattern.webp";
  const contactHeader = "/image/contact-header.webp";

  const [formData, setFormData] = useState({
    namaDepan: '',
    namaBelakang: '',
    email: '',
    whatsapp: '',
    domisili: '',
    pengalaman: ''
  });

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [phoneError, setPhoneError] = useState<string>('');

  const contactItems = [
    {
      icon: "ic:baseline-whatsapp",
      label: "WhatsApp",
      value: "08123593150",
    },
    {
      icon: "ic:baseline-email",
      label: "Email",
      value: "partner@pekalenrafting.com",
    },
    {
      icon: "ic:baseline-location-on",
      label: "Kantor",
      value: "Jl. Pekalen Adventure No. 123, Probolinggo, Jawa Timur",
    },
    {
      icon: "ic:baseline-access-time",
      label: "Jam Operasional",
      value: "Senin - Jumat: 08:00 - 17:00 WIB",
    },
  ];

  const formatPhone = (phone: string) => {
    if(phone.startsWith('0')){ 
      return '62' + phone.slice(1)
    } else if (phone.startsWith('+62')){
      return phone.slice(1)
    }
    return phone;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate phone before submit
    if (!handleValidatePhone(formData.whatsapp)) {
      return;
    }

    setIsSubmitting(true);

    try {
          const message = `*Pendaftaran Agent Pekalen Rafting*

        *Nama:* ${formData.namaDepan} ${formData.namaBelakang}
        *Email:* ${formData.email}
        *No. WhatsApp:* ${formatPhone(formData.whatsapp)}
        *Domisili:* ${formData.domisili}
        *Pengalaman Penjualan:* ${formData.pengalaman || '-'}`;

        // Encode message untuk URL
        const encodedMessage = encodeURIComponent(message);
        const whatsappUrl = `https://wa.me/6285134567402?text=${encodedMessage}`;

        const payload = {
            first_name:formData.namaDepan,
            last_name:formData.namaBelakang,
            email:formData.email,
            phone:formatPhone(formData.whatsapp),
            address:formData.domisili,
            experience:formData.pengalaman
        };

        await clientPost(API_ENDPOINTS.register, {
            body: JSON.stringify(payload)
          });

        // Buka WhatsApp
        setFormData({
            namaDepan: '',
            namaBelakang: '',
            email: '',
            whatsapp: '',
            domisili: '',
            pengalaman: ''
          })
        toast.success('Form berhasil dikirim! Mengalihkan ke WhatsApp...');

        setTimeout(()=>{
          setIsSubmitting(false);
          window.open(whatsappUrl, '_blank');
        }, 3000)
    } catch (error) {
        toast.error('Gagal mengirim form. Silakan coba lagi.');
        setIsSubmitting(false);
    }
    // Format pesan WhatsApp

  };

  const handleValidatePhone = (phone: string): boolean => {
    if (phone.length === 0) {
      setPhoneError('');
      return true;
    }
    if (!phone.startsWith('08') && !phone.startsWith('+62') && !phone.startsWith('62')) {
      setPhoneError('Nomor WhatsApp harus diawali dengan 08, 62, atau +62');
      return false;
    }
    setPhoneError('');
    return true;
  };


  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div
      id="contact"
      data-aos="fade-up"
      className="relative w-full shadow-md bg-[#fafafa] p-6 md:p-8 lg:p-0 lg:py-12"
    >
      {/* Pattern Background */}
      <div className="absolute inset-0 opacity-2">
        <Image
          src={pattern}
          alt="background pattern"
          fill
          className="object-cover"
          loading="lazy"
          quality={30}
          sizes="100vw"
        />
      </div>

      {/* Background Image */}
      <div className="absolute top-0 left-0 right-0 md:h-[200px] h-[400px]">
        <Image
          src={contactHeader}
          alt="contact header"
          fill
          className="object-cover"
          loading="lazy"
          quality={85}
        />
        <div className="absolute inset-0 bg-black/70" />
      </div>
      {/* Content Container Mobile & Tablet */}
      <div className="relative flex flex-col gap-6 justify-center items-center max-w-6xl mx-auto lg:hidden">
        {/* Section Title */}
        <h2 className="font-bold md:text-[32px] text-[24px] text-center">
          Tunggu apa lagi? Yuk jadi agent Pekalen Rafting
        </h2>
        {/* Contact Form */}
        <form onSubmit={handleSubmit} className=" bg-white border border-black/10 rounded-xl flex flex-col gap-8 p-8 w-full">
          {/* Form Header */}
          <div className="flex flex-col gap-3 text-center">
            <p className="font-bold text-[32px] leading-7 text-[#101828] mb-3">
              Daftar Sekarang
            </p>
            <p className="font-family-open-sans font-normal text-[14px] leading-5 text-[#4a5565] m-0">
              Isi form di bawah ini dan tim kami akan menghubungi Anda dalam 24
              jam
            </p>
          </div>
          {/* Form Fields */}
          <div className="flex flex-col gap-5">
            {/* Name Fields */}
            <div className="flex flex-col gap-5">
              <div className="flex flex-col gap-2">
                <p className="font-family-open-sans text-xs leading-3 text-base-black">
                  Nama Depan
                </p>
                <input
                  name="namaDepan"
                  value={formData.namaDepan}
                  onChange={handleChange}
                  placeholder="Masukkan Nama Depan"
                  disabled={isSubmitting}
                  required
                  className="w-full bg-[#f3f3f5] rounded-lg border-none p-3 font-family-open-sans text-[12px] box-border placeholder:text-base-grey text-base-black"
                />
              </div>
              <div className="flex flex-col gap-2">
                <p className="font-family-open-sans text-xs leading-3 text-base-black">
                  Nama Belakang
                </p>
                <input
                  name="namaBelakang"
                  value={formData.namaBelakang}
                  onChange={handleChange}
                  placeholder="Masukkan Nama Belakang"
                  disabled={isSubmitting}
                  required
                  className="w-full bg-[#f3f3f5] rounded-lg border-none p-3 font-family-open-sans text-[12px] box-border placeholder:text-base-grey text-base-black"
                />
              </div>
            </div>

            {/* Email */}
            <div className="flex flex-col gap-2">
              <p className="font-family-open-sans text-xs leading-3 text-base-black">
                Email
              </p>
              <input
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="user@domain.com"
                disabled={isSubmitting}
                type="email"
                required
                className="w-full bg-[#f3f3f5] rounded-lg border-none p-3 font-family-open-sans text-[12px] box-border placeholder:text-base-grey text-base-black"
              />
            </div>

            {/* WhatsApp */}
            <div className="flex flex-col gap-2">
              <p className="font-family-open-sans text-xs leading-3 text-base-black">
                No. WhatsApp
              </p>
              <input
                name="whatsapp"
                value={formData.whatsapp}
                onChange={(e) => {
                  handleChange(e);
                  handleValidatePhone(e.target.value);
                }}
                placeholder="08xxxxxxxxxx / 62xxxxxxxxxx"
                disabled={isSubmitting}
                type='number'
                inputMode='numeric'
                pattern="[0-9]*"
                required
                className={`w-full bg-[#f3f3f5] rounded-lg border p-3 font-family-open-sans text-[12px] box-border placeholder:text-base-grey text-base-black ${phoneError ? 'border-red-500 focus:outline-red-500' : 'border-transparent'}`}
              />
              {phoneError && (    
                <p className="text-red-500 text-xs font-family-open-sans">{phoneError}</p>
              )}
            </div>

            {/* Domisili */}
            <div className="flex flex-col gap-2">
              <p className="font-family-open-sans text-xs leading-3 text-base-black">
                Domisili
              </p>
              <input
                name="domisili"
                value={formData.domisili}
                onChange={handleChange}
                placeholder="Kota Tempat Tinggal"
                disabled={isSubmitting}
                required
                className="w-full bg-[#f3f3f5] rounded-lg border-none p-3 font-family-open-sans text-[12px] box-border placeholder:text-base-grey text-base-black"
              />
            </div>

            {/* Experience */}
            <div className="flex flex-col gap-2">
              <p className="font-family-open-sans text-xs leading-3 text-base-black">
                Pengalaman Penjualan (opsional)
              </p>
              <textarea
                name="pengalaman"
                value={formData.pengalaman}
                onChange={handleChange}
                placeholder="Ceritakan Pengalaman Anda di Bidang Penjualan atau Marketing"
                disabled={isSubmitting}
                rows={3}
                className="w-full bg-[#f3f3f5] rounded-lg border-none p-3 font-family-open-sans text-[12px] box-border resize-y placeholder:text-base-grey text-base-black"
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex flex-col gap-3">
            <button disabled={isSubmitting} type="submit" className="w-full bg-[#fbe64b] text-[18px] text-base-black border-2 border-white rounded-full md:px-[18px] py-0.5 h-14 flex gap-2 items-center justify-center font-family-open-sans font-bold text-lg cursor-pointer hover:bg-[#fcd400] transition-colors">
             {isSubmitting ?  'Memproses...' : 'Gabung Sebagai Agent'} 
              <div className="bg-base-black rounded-full w-9 h-9 flex items-center justify-center">
                <Icon
                  icon="ph:arrow-line-up-right"
                  fontSize={20}
                  color="#ffffff"
                />
              </div>
            </button>
            <p className="font-family-open-sans text-[10px] leading-[14px] text-[#6a7282] text-center">
              Dengan mendaftar, Anda menyetujui syarat dan ketentuan yang
              berlaku
            </p>
          </div>
        </form>
        {/* Contact Information */}
        <div className="flex flex-col gap-12 items-center mt-8">
          <Image
            width={100}
            height={100}
            src={logoUrl}
            alt="Pekalen Rafting Probolinggo"
            className="h-[36.17px] w-[186.8px] cursor-pointer mx-auto"
          />
          {/* Contact Items */}
          <div className="flex flex-col gap-8 font-family-open-sans justify-center items-center">
            <p className="font-family-open-sans md:text-left text-center font-bold text-[32px] tracking-[0.32px] text-base-black m-0">
              Contact Us
            </p>
            <div className="grid grid-cols-2 gap-y-6 gap-x-4">
              {contactItems.map((item, index) => (
                <div
                  key={index}
                  className="flex flex-col gap-4 items-center col-span-1"
                >
                  <div className="w-[58px] h-[58px] bg-base-black border-[3px] border-white flex items-center justify-center">
                    <Icon icon={item.icon} fontSize={24} color="#FBE64B" />
                  </div>
                  <div>
                    <p className="font-family-open-sans text-center  font-bold text-base leading-5 text-base-black m-0">
                      {item.label}
                    </p>
                    <p className="font-family-open-sans text-center font-normal text-sm leading-5 text-base-grey m-0">
                      {item.value}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          {/* Call to Action */}
          <div className="flex flex-col gap-4 justify-center items-center">
            <p className="font-family-open-sans font-bold text-lg text-center leading-6 text-base-black">
              Ingin tahu lebih lanjut tentang agent Pekalen Rafting?
            </p>
            <button className="bg-white border-2 border-[#e5e5e5] rounded-full px-[18px] py-0.5 h-14 flex gap-2 items-center font-family-open-sans font-bold text-lg text-base-black cursor-pointer hover:bg-white/90 transition-colors">
              Hubungi Kami
              <div className="bg-base-black rounded-full w-9 h-9 flex items-center justify-center">
                <Icon
                  icon="ph:arrow-line-up-right"
                  fontSize={20}
                  color="#ffffff"
                />
              </div>
            </button>
          </div>
        </div>
      </div>
      {/* Content Container Desktop */}
      <div className="relative hidden lg:flex flex-col gap-6 justify-center items-center max-w-6xl mx-auto">
        <div className="flex flex-row gap-12 w-full">
          <div className="flex flex-col justify-between">
            {/* Section Title */}
            <h2 className="font-bold lg:text-[36px] md:text-[32px] text-[24px] text-left">
              Tunggu apa lagi? Yuk jadi agent Pekalen Rafting
            </h2>
            {/* Contact Information */}
            <div className="flex flex-col gap-12 items-start">
              <Image
                src={logoUrl}
                width={100}
                height={100}
                alt="Pekalen Rafting Probolinggo"
                className="h-[36.17px] w-[186.8px] cursor-pointer"
              />
              {/* Contact Items */}
              <div className="flex flex-col gap-6 font-family-open-sans justify-center items-center">
                <p className="w-full font-family-open-sans md:text-left text-center font-bold text-[32px] tracking-[0.32px] text-base-black m-0">
                  Contact Us
                </p>
                <div className="grid grid-cols-1 gap-y-4 gap-x-4">
                  {contactItems.map((item, index) => (
                    <div
                      key={index}
                      className="flex flex-row gap-4 items-center col-span-1"
                    >
                      <div className="w-[58px] h-[58px] aspect-square bg-base-black border-[3px] border-white flex items-center justify-center">
                        <Icon icon={item.icon} fontSize={24} color="#FBE64B" />
                      </div>
                      <div>
                        <p className="font-family-open-sans text-left  font-bold text-base leading-5 text-base-black m-0">
                          {item.label}
                        </p>
                        <p className="font-family-open-sans text-left font-normal text-sm leading-5 text-base-grey m-0">
                          {item.value}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          {/* Contact Form */}
          <form onSubmit={handleSubmit} className=" bg-white border border-black/10 rounded-xl flex flex-col gap-8 p-8 w-full">
            {/* Form Header */}
            <div className="flex flex-col gap-3 text-center">
              <p className="font-bold text-[32px] leading-7 text-[#101828] mb-3">
                Daftar Sekarang
              </p>
              <p className="font-family-open-sans font-normal text-sm leading-5 text-[#4a5565] m-0">
                Isi form di bawah ini dan tim kami akan menghubungi Anda dalam
                24 jam
              </p>
            </div>
            {/* Form Fields */}
            <div className="flex flex-col gap-5">
              {/* Name Fields */}
              <div className="flex flex-col lg:flex-row gap-5 lg:w-full">
                <div className="flex flex-col gap-2 lg:w-full">
                  <p className="font-family-open-sans text-xs leading-3 text-base-black">
                    Nama Depan
                  </p>
                  <input
                    name="namaDepan"
                    value={formData.namaDepan}
                    onChange={handleChange}
                    placeholder="Masukkan Nama Depan"
                    disabled={isSubmitting}
                    required
                    className="w-full bg-[#f3f3f5] rounded-lg border-none p-3 font-family-open-sans text-sm box-border placeholder:text-base-grey text-base-black"
                  />
                </div>
                <div className="flex flex-col gap-2 lg:w-full">
                  <p className="font-family-open-sans text-xs leading-3 text-base-black">
                    Nama Belakang
                  </p>
                  <input
                    name="namaBelakang"
                    value={formData.namaBelakang}
                    onChange={handleChange}
                    placeholder="Masukkan Nama Belakang"
                    disabled={isSubmitting}
                    required
                    className="w-full bg-[#f3f3f5] rounded-lg border-none p-3 font-family-open-sans text-sm box-border placeholder:text-base-grey text-base-black"
                  />
                </div>
              </div>

              {/* Email */}
              <div className="flex flex-col gap-2">
                <p className="font-family-open-sans text-xs leading-3 text-base-black">
                  Email
                </p>
                <input
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="user@domain.com"
                  disabled={isSubmitting}
                  type="email"
                  required
                  className="w-full bg-[#f3f3f5] rounded-lg border-none p-3 font-family-open-sans text-sm box-border placeholder:text-base-grey text-base-black"
                />
              </div>

              {/* WhatsApp */}
              <div className="flex flex-col gap-2">
                <p className="font-family-open-sans text-xs leading-3 text-base-black">
                  No. WhatsApp
                </p>
                <input
                  name="whatsapp"
                  value={formData.whatsapp}
                  onChange={(e) => {
                    handleChange(e);
                    handleValidatePhone(e.target.value);
                  }}
                  placeholder="08xxxxxxxxxx / 62xxxxxxxxxx"
                  disabled={isSubmitting}
                  type='number'
                  inputMode='numeric'
                  pattern="[0-9]*"
                  required
                  className={`w-full bg-[#f3f3f5] rounded-lg border p-3 font-family-open-sans text-sm box-border placeholder:text-base-grey text-base-black ${phoneError ? 'border-red-500 focus:outline-red-500' : 'border-transparent'}`}
                />
                {phoneError && (
                  <p className="text-red-500 text-xs font-family-open-sans">{phoneError}</p>
                )}
              </div>

              {/* Domisili */}
              <div className="flex flex-col gap-2">
                <p className="font-family-open-sans text-xs leading-3 text-base-black">
                  Domisili
                </p>
                <input
                  name="domisili"
                  value={formData.domisili}
                  onChange={handleChange}
                  placeholder="Kota Tempat Tinggal"
                  disabled={isSubmitting}
                  required
                  className="w-full bg-[#f3f3f5] rounded-lg border-none p-3 font-family-open-sans text-sm box-border placeholder:text-base-grey text-base-black"
                />
              </div>

              {/* Experience */}
              <div className="flex flex-col gap-2">
                <p className="font-family-open-sans text-xs leading-3 text-base-black">
                  Pengalaman Penjualan (opsional)
                </p>
                <textarea
                  name="pengalaman"
                  value={formData.pengalaman}
                  onChange={handleChange}
                  placeholder="Ceritakan Pengalaman Anda di Bidang Penjualan atau Marketing"
                  disabled={isSubmitting}
                  rows={3}
                  className="w-full bg-[#f3f3f5] rounded-lg border-none p-3 font-family-open-sans text-sm box-border resize-y placeholder:text-base-grey text-base-black"
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex flex-col gap-3">
              <button disabled={isSubmitting} type="submit" className="w-full bg-[#fbe64b] text-base-black border-2 border-white rounded-full px-[18px] py-0.5 h-14 flex gap-2 items-center justify-center font-family-open-sans font-bold text-lg cursor-pointer hover:bg-[#fcd400] transition-colors">
             {isSubmitting ?  'Memproses...' : 'Gabung Sebagai Agent'} 
                <div className="bg-base-black rounded-full w-9 h-9 flex items-center justify-center">
                  <Icon
                    icon="ph:arrow-line-up-right"
                    fontSize={20}
                    color="#ffffff"
                  />
                </div>
              </button>
              <p className="font-family-open-sans text-[10px] leading-[14px] text-[#6a7282] text-center mt-3">
                Dengan mendaftar, Anda menyetujui syarat dan ketentuan yang
                berlaku
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SectionContact;
